import { useCallback, useEffect, useRef, useState } from 'react'
import type { Denial, District, DistrictImport, GeoLayerVersion } from './api'
import { denialText, translate, type Lang } from './i18n'
import { fmtDateTime, zoneLabel } from './time'

/**
 * Carga de áreas de la agencia en GeoJSON (§16.3).
 *
 * Sirve para las dos que hay, porque son el mismo dato con otro propósito: los **límites de los
 * distritos**, con los que de qué división es un hotspot sale de dónde está y no del beat anotado
 * en sus incidentes, y las **zonas de patrullaje**, con las que «fuera de zona» se mide contra la
 * forma del parque y no contra un radio.
 *
 * Las dos empiezan vacías en toda agencia, y en las dos eso no es una falla sino el respaldo
 * funcionando: el prefijo del beat en una, el radio en la otra. La pantalla lo dice, para que
 * nadie la abra creyendo que está rota.
 *
 * `prefix` elige el juego de textos. Los rechazos se comparten a propósito: el servidor devuelve
 * los mismos códigos para las dos, porque las lee el mismo lector.
 */
/** Fecha y hora legibles; «desde siempre» para lo que ya estaba antes de que hubiera versiones. */
const cuando = (lang: Lang, iso: string): string =>
  iso.startsWith('0001-')
    ? translate(lang, 'geo.always')
    : fmtDateTime(iso, lang)

export function GeoAreaEditor({
  lang, prefix, areas, busy, onUpload, onLoaded, loadVersions,
}: {
  lang: Lang
  /** Cuál de las dos: elige títulos, ayuda y etiquetas. */
  prefix: 'districts' | 'zones'
  areas: District[]
  busy: boolean
  /** `validFrom` como lo escribió (`datetime-local`), en la hora que se muestra; o nada para «desde ahora». */
  onUpload: (geoJson: string, validFrom?: string) => Promise<DistrictImport>
  /** Para recargar la lista: lo que se acaba de subir ya cambió lo que se está mirando. */
  onLoaded: () => void
  /** La historia de cargas (§8.3). Sin esto la pantalla no la muestra. */
  loadVersions?: () => Promise<GeoLayerVersion[]>
}) {
  const [subiendo, setSubiendo] = useState(false)
  const [desde, setDesde] = useState('')
  const [versiones, setVersiones] = useState<GeoLayerVersion[]>([])

  const recargarVersiones = useCallback(async () => {
    if (!loadVersions) return
    try { setVersiones(await loadVersions()) } catch { /* la historia es informativa: su falla no tapa la carga */ }
  }, [loadVersions])
  useEffect(() => { void recargarVersiones() }, [recargarVersiones])
  const [resultado, setResultado] = useState<DistrictImport | null>(null)
  const [problema, setProblema] = useState<Denial | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const elegir = async (file: File | undefined) => {
    if (!file || subiendo) return
    setSubiendo(true)
    setResultado(null)
    setProblema(null)
    try {
      setResultado(await onUpload(await file.text(), desde || undefined))
      if (input.current) input.current.value = ''
      onLoaded()
      void recargarVersiones()
    } catch (e) {
      setProblema(e instanceof Error && 'denial' in e ? (e as { denial: Denial }).denial : null)
    } finally {
      setSubiendo(false)
    }
  }

  return (
    <section className="panel">
      <h2>{translate(lang, `${prefix}.title`)}</h2>
      <p className="notice">{translate(lang, `${prefix}.help`)}</p>

      {/* La fecha va antes que el archivo: el archivo se sube al elegirlo. */}
      <label className="rule">
        {/* Se escribe y se lee en la hora que la pantalla está mostrando: se dice cuál (H-01). */}
        {translate(lang, 'geo.validFrom')} ({zoneLabel()})
        <input
          type="datetime-local" value={desde} disabled={busy || subiendo}
          onChange={(e) => setDesde(e.target.value)}
        />
      </label>
      <p className="notice">{translate(lang, 'geo.validFromHelp')}</p>

      <div className="row-line" style={{ flexWrap: 'wrap' }}>
        {/* Se sube al elegir el archivo, sin botón aparte: no hay nada que ajustar entre una
            cosa y la otra, y un botón de más es un paso que alguien se olvida de dar. */}
        <input
          ref={input}
          type="file"
          accept=".geojson,.json,application/geo+json,application/json"
          style={{ maxWidth: '100%' }}
          disabled={busy || subiendo}
          onChange={(e) => void elegir(e.target.files?.[0])}
          aria-label={translate(lang, `${prefix}.file`)}
        />
        {subiendo && <span className="notice">{translate(lang, `${prefix}.uploading`)}</span>}
      </div>

      {problema && <p className="notice error">{denialText(lang, problema)}</p>}

      {resultado && (
        <div className="card">
          <p><strong>{translate(lang, `${prefix}.loaded`, [resultado.written, resultado.read])}</strong></p>
          {resultado.version !== undefined && resultado.validFrom && (() => {
            // Programada: dice cuándo empieza y qué sigue rigiendo hasta entonces. «Vigente desde»
            // mañana se leía como si ya rigiera (PD-02).
            if (Date.parse(resultado.validFrom) <= Date.now())
              return <p className="meta">{translate(lang, 'geo.version', [resultado.version, cuando(lang, resultado.validFrom)])}</p>
            const actual = versiones.find((v) => v.validTo === resultado.validFrom)
            return (
              <p className="notice">
                {actual
                  ? translate(lang, 'geo.versionScheduled', [resultado.version, cuando(lang, resultado.validFrom), actual.version])
                  : translate(lang, 'geo.versionScheduledFirst', [resultado.version, cuando(lang, resultado.validFrom)])}
              </p>
            )
          })()}
          {/* Si ya había una carga programada después, esta rige solo hasta entonces, y quien carga
              tiene que saberlo en el momento (hallazgo H-03). */}
          {(() => {
            const recien = versiones.find((v) => v.version === resultado.version)
            return recien?.validTo
              ? <p className="notice warning">{translate(lang, 'geo.overridden', [cuando(lang, recien.validTo)])}</p>
              : null
          })()}
          {resultado.problems.length > 0 && (
            <>
              <p className="notice warning">
                {translate(lang, `${prefix}.rejected`, [resultado.problems.length])}
              </p>
              {/* Con la posición en el archivo: es lo que hace falta para ir a corregirlo. */}
              <ul className="meta">
                {resultado.problems.slice(0, 20).map((p) => (
                  <li key={`${p.index}-${p.district}`}>
                    {translate(lang, `${prefix}.entry`, [p.index])}
                    {p.district && ` · ${p.district}`}
                    {' · '}
                    {translate(lang, `districts.problem.${p.code}`)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {areas.length === 0
        ? <p className="notice">{translate(lang, `${prefix}.none`)}</p>
        : (
          <p className="meta">
            {areas.map((d) => `${d.code} (${d.name})`).join(' · ')}
          </p>
        )}

      {versiones.length > 0 && (
        <table className="compare">
          <thead>
            <tr>
              <th>{translate(lang, 'geo.colVersion')}</th>
              <th>{translate(lang, 'geo.colFrom')}</th>
              <th>{translate(lang, 'geo.colTo')}</th>
              <th>{translate(lang, 'geo.colAreas')}</th>
              <th>{translate(lang, 'geo.colBy')}</th>
            </tr>
          </thead>
          <tbody>
            {versiones.map((v) => (
              <tr key={v.version}>
                <td>{v.version}</td>
                <td>{cuando(lang, v.validFrom)}</td>
                <td>
                  {v.validTo
                    ? cuando(lang, v.validTo)
                    // Sin fin, pero todavía no empieza: no rige, está programada (hallazgo H-02).
                    : translate(lang, Date.parse(v.validFrom) > Date.now() ? 'geo.scheduled' : 'geo.open')}
                </td>
                <td>{v.areas}</td>
                <td>{v.loadedBy || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
